<template>
  <div class="michelson-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$ΔL = 2(d₂ - d₁) = nλ$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>反射镜 M₂ 移动 (Δd)</label>
            <span class="val">{{ (d2/10).toFixed(2) }} <small>μm</small></span>
          </div>
          <div class="hud-item orange">
             <label>干涉级数变化 ΔN</label>
             <span class="val">{{ Math.abs((2 * d2 * 10) / 633).toFixed(1) }} <small>条条</small></span>
          </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-laser"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <!-- 动态干涉条纹纹理 -->
             <radialGradient id="fringe-pattern" cx="50%" cy="50%" r="50%">
                <stop v-for="n in 6" :key="n" 
                      :offset="`${(n-1)*20}%`" 
                      :stop-color="getFringeColor(n)" />
             </radialGradient>
          </defs>

          <!-- 激光源 (He-Ne Laser) -->
          <rect x="20" y="115" width="40" height="10" fill="#f43f5e" rx="2" />
          <line x1="60" y1="120" x2="200" y2="120" stroke="#f43f5e" stroke-width="2" filter="url(#glow-laser)" />

          <!-- 分束镜 (Beam Splitter) -->
          <line x1="185" y1="105" x2="215" y2="135" stroke="#94a3b8" stroke-width="3" opacity="0.6" />
          <text x="175" y="150" fill="#475569" font-size="8" font-weight="900">分束镜 BS</text>

          <!-- 路程 1 (固定镜 M1) -->
          <path d="M 200,120 V 45" stroke="#f43f5e" stroke-width="1.5" stroke-dasharray="4,2" />
          <rect x="185" y="35" width="30" height="5" fill="#334155" stroke="#94a3b8" />
          <text x="220" y="42" fill="#64748b" font-size="8">M₁ (参考镜)</text>

          <!-- 路程 2 (动镜 M2) -->
          <path :d="`M 200,120 H ${320 + d2/2}`" stroke="#f43f5e" stroke-width="1.5" />
          <g :transform="`translate(${320 + d2/2}, 105)`">
             <rect width="5" height="30" fill="#1e293b" stroke="#f43f5e" stroke-width="1.5" />
             <path d="M 8,0 L 15,5 L 8,10" fill="none" stroke="#f43f5e" />
          </g>
          <text :x="280" y="150" fill="#f43f5e" font-size="8" font-weight="900">M₂ (测量镜)</text>

          <!-- 观测屏 (Observation Screen) -->
          <g transform="translate(170, 190)">
             <rect width="60" height="60" fill="#000" stroke="#1e293b" rx="4" />
             <!-- 干涉条纹动态显示区 -->
             <circle cx="30" cy="30" r="25" fill="url(#fringe-pattern)" />
             <text x="30" y="72" text-anchor="middle" fill="#475569" font-size="8">等倾干涉图样</text>
          </g>
          <line x1="200" y1="135" x2="200" y2="190" stroke="#f43f5e" stroke-width="2" opacity="0.8" />
        </svg>

        <div class="phase-info">
           相位差 Δφ ≈ {{ ((d2*2*Math.PI)/6.33).toFixed(1) }} rad
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">微调反射镜 M₂ 位置 (纳米级)</label>
                 <el-slider v-model="d2" :min="-200" :max="200" :step="0.5" size="small"></el-slider>
              </div>
              <el-button type="danger" size="mini" plain icon="el-icon-refresh-left" @click="d2 = 0">归零平衡</el-button>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-view"></i>
           <p><strong>核心揭示：</strong>迈克耳孙干涉仪将光束“一分为二”。由于 M₂ 的微小移动，导致两束光回到分束镜时产生了<strong>光程差</strong>。波峰与波峰相遇则增强，波峰与波谷相遇则相消。<strong>注意观察：</strong>当你滑动滑块，观测屏中央的条纹会源源不断地生成或消失，这就是用光波长替代物理尺进行精密测量的本质。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'MichelsonInterferometerViz',
  data() {
    return {
      d2: 0, // In relative nano-scale units for visualization
      lambda: 633 // He-Ne laser wavelength in nm
    }
  },
  methods: {
    getFringeColor(n) {
       // Mock interference intensity based on d2 and radial distance
       // Phase = (2 * PI * 2 * d2 / lambda) + radial_offset
       const phaseBase = (4 * Math.PI * this.d2 * 10) / this.lambda;
       const intensity = (1 + Math.cos(phaseBase + n * 0.8)) / 2;
       return `rgba(244, 63, 94, ${0.05 + intensity * 0.85})`;
    }
  }
}
</script>

<style scoped lang="scss">
.michelson-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; &.cyan { border-left: 3px solid #f43f5e; } &.orange { border-left: 3px solid #f97316; } label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } } }
.phase-info { position: absolute; bottom: 12px; right: 12px; color: #334155; font-size: 10px; font-weight: 900; background: rgba(2,6,23,0.8); padding: 2px 8px; border-radius: 4px; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .flex-row { flex-direction: row; } .flex-1 { flex: 1; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #fef2f2; padding: 10px 14px; border-radius: 10px; border: 1px solid #fee2e2; display: flex; align-items: center; gap: 10px; i { color: #dc2626; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #991b1b; line-height: 1.5; strong { color: #7f1d1d; } } }
</style>
